import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TransactionStatus = ({ 
  status, 
  transactionData, 
  onRetry, 
  onCancel,
  onWhatsAppReceipt 
}) => {
  const [countdown, setCountdown] = useState(30);
  const navigate = useNavigate();

  useEffect(() => {
    if (status === 'processing' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [status, countdown]);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-CM', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const getStatusConfig = () => {
    switch (status) {
      case 'processing':
        return {
          icon: 'Loader2',
          iconClass: 'text-primary animate-spin',
          bgClass: 'bg-primary/10 border-primary/20',
          title: 'Processing Payment',
          message: 'Please wait while we process your payment...',
          showCountdown: true
        };
      case 'success':
        return {
          icon: 'CheckCircle',
          iconClass: 'text-success',
          bgClass: 'bg-success/10 border-success/20',
          title: 'Payment Successful!',
          message: 'Your payment has been processed successfully.',
          showActions: true
        };
      case 'failed':
        return {
          icon: 'XCircle',
          iconClass: 'text-error',
          bgClass: 'bg-error/10 border-error/20',
          title: 'Payment Failed',
          message: 'There was an issue processing your payment. Please try again.',
          showRetry: true
        };
      case 'pending':
        return {
          icon: 'Clock',
          iconClass: 'text-warning',
          bgClass: 'bg-warning/10 border-warning/20',
          title: 'Payment Pending',
          message: 'Your payment is being verified. This may take a few minutes.',
          showWait: true
        };
      default:
        return {
          icon: 'AlertCircle',
          iconClass: 'text-muted-foreground',
          bgClass: 'bg-muted/10 border-border',
          title: 'Unknown Status',
          message: 'Please contact support for assistance.'
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="text-center">
        {/* Status Icon */}
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 border ${config?.bgClass}`}>
          <Icon name={config?.icon} size={40} className={config?.iconClass} />
        </div>

        {/* Status Title */}
        <h2 className="text-2xl font-bold text-foreground mb-2">{config?.title}</h2>
        
        {/* Status Message */}
        <p className="text-muted-foreground mb-6">{config?.message}</p>

        {/* Transaction Details */}
        {transactionData && (
          <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-medium text-foreground mb-3">Transaction Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Transaction ID:</span>
                <span className="font-mono text-foreground">{transactionData?.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount:</span>
                <span className="font-mono text-foreground">{formatAmount(transactionData?.amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Payment Method:</span>
                <span className="text-foreground">{transactionData?.method}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Date & Time:</span>
                <span className="text-foreground">
                  {new Date(transactionData.timestamp)?.toLocaleString('fr-CM')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Processing Countdown */}
        {config?.showCountdown && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center space-x-2">
              <Icon name="Clock" size={16} className="text-primary" />
              <span className="text-sm text-primary">
                Timeout in {countdown} seconds
              </span>
            </div>
          </div>
        )}

        {/* Mobile Money Instructions */}
        {status === 'processing' && transactionData?.method?.includes('Mobile Money') && (
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-2">
              <Icon name="Smartphone" size={16} className="text-accent mt-0.5" />
              <div className="text-left">
                <p className="text-sm font-medium text-accent mb-1">Check Your Phone</p>
                <p className="text-xs text-muted-foreground">
                  A payment request has been sent to your mobile money account. 
                  Please enter your PIN to complete the transaction.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Success Actions */}
        {config?.showActions && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={onWhatsAppReceipt}
                iconName="MessageCircle"
                iconPosition="left"
                iconSize={16}
              >
                Send Receipt via WhatsApp
              </Button>
              <Button
                variant="outline"
                onClick={() => window.print()}
                iconName="Printer"
                iconPosition="left"
                iconSize={16}
              >
                Print Receipt
              </Button>
            </div>
            
            <Button
              variant="default"
              fullWidth
              onClick={() => navigate('/subscription-management')}
              iconName="ArrowRight"
              iconPosition="right"
              iconSize={16}
            >
              Continue to Dashboard
            </Button>
          </div>
        )}

        {/* Retry Actions */}
        {config?.showRetry && (
          <div className="space-y-3">
            <Button
              variant="default"
              fullWidth
              onClick={onRetry}
              iconName="RotateCcw"
              iconPosition="left"
              iconSize={16}
            >
              Try Again
            </Button>
            <Button
              variant="outline"
              fullWidth
              onClick={onCancel}
            >
              Cancel Payment
            </Button>
          </div>
        )}

        {/* Pending Actions */}
        {config?.showWait && (
          <div className="space-y-3">
            <Button
              variant="outline"
              fullWidth
              onClick={() => window.location?.reload()}
              iconName="RefreshCw"
              iconPosition="left"
              iconSize={16}
            >
              Check Status
            </Button>
            <Button
              variant="ghost"
              fullWidth
              onClick={onCancel}
            >
              Cancel & Return
            </Button>
          </div>
        )}

        {/* Support Contact */}
        <div className="mt-6 pt-6 border-t border-border">
          <p className="text-xs text-muted-foreground mb-2">Need help?</p>
          <div className="flex items-center justify-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              iconName="MessageCircle"
              iconPosition="left"
              iconSize={14}
            >
              WhatsApp Support
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="Phone"
              iconPosition="left"
              iconSize={14}
            >
              Call Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionStatus;